import React, { useState, useEffect } from "react";
import HeroSection from "./EvangelismAssistant/HeroSection";
import ChatInterface from "./EvangelismAssistant/ChatInterface";
import { WorldviewSelector } from "./EvangelismAssistant/WorldviewSelector";
import { EvangelismAssistantProvider } from "./EvangelismAssistant/EvangelismAssistantProvider";
import WorldviewInstructionsDialog from "./EvangelismAssistant/WorldviewInstructionsDialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

type Difficulty = "beginner" | "intermediate" | "difficult";

const EvangelismAssistantTool = () => {
  const [selectedWorldview, setSelectedWorldview] = useState<string | null>(null);
  const [selectedDifficulty, setSelectedDifficulty] = useState<Difficulty | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  
  // Force render when component mounts to ensure visibility in full-screen mode
  useEffect(() => {
    // Short delay to ensure DOM is ready
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 200);
    
    return () => clearTimeout(timer);
  }, []);
  
  // Show dialog after difficulty is selected
  useEffect(() => {
    if (selectedWorldview && selectedDifficulty && !openDialog) {
      setOpenDialog(true);
    }
  }, [selectedWorldview, selectedDifficulty]);

  const handleStartConversation = () => {
    console.log("Starting conversation - closing dialog");
    setOpenDialog(false);
  };
  
  const handleReset = () => {
    setSelectedWorldview(null);
    setSelectedDifficulty(null);
    setOpenDialog(false);
  };
  
  return (
    <div className="max-w-5xl mx-auto pb-12">
      <EvangelismAssistantProvider>
        <HeroSection />
        
        {/* Worldview Instructions Dialog - shown after difficulty selection */}
        <WorldviewInstructionsDialog 
          open={openDialog}
          onOpenChange={setOpenDialog}
          worldview={selectedWorldview}
          onStartConversation={handleStartConversation}
        />
        
        {!selectedWorldview ? (
          <WorldviewSelector onWorldviewSelect={setSelectedWorldview} />
        ) : !selectedDifficulty ? (
          <DifficultySelector onSelectDifficulty={setSelectedDifficulty} />
        ) : (
          <ChatInterface 
            worldview={selectedWorldview} 
            difficulty={selectedDifficulty}
            onReset={handleReset} 
          />
        )}
      </EvangelismAssistantProvider>
    </div>
  );
};

const DifficultySelector: React.FC<{ onSelectDifficulty: (difficulty: Difficulty) => void }> = ({ 
  onSelectDifficulty 
}) => {
  return (
    <div className="container mx-auto px-4 pb-16">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-[#403E43] mb-4">Choose Difficulty Level</h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Select how receptive your conversation partner will be to the Gospel. 
          This affects how quickly they progress toward conversion.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
        <Card className="border-green-200 hover:border-green-300 transition-all cursor-pointer group">
          <CardHeader className="text-center">
            <CardTitle className="text-green-700 group-hover:text-green-800">
              🟢 Beginner
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-sm text-gray-600">
              The person is more open to hearing the Gospel and will respond positively to clear biblical truth.
            </p>
            <Button 
              onClick={() => onSelectDifficulty("beginner")}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              Start Beginner Mode
            </Button>
          </CardContent>
        </Card>

        <Card className="border-orange-200 hover:border-orange-300 transition-all cursor-pointer group">
          <CardHeader className="text-center">
            <CardTitle className="text-orange-700 group-hover:text-orange-800">
              🟡 Intermediate
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-sm text-gray-600">
              The person has typical resistance but will gradually soften with consistent, biblical responses.
            </p>
            <Button 
              onClick={() => onSelectDifficulty("intermediate")}
              className="w-full bg-orange-600 hover:bg-orange-700"
            >
              Start Intermediate Mode
            </Button>
          </CardContent>
        </Card>

        <Card className="border-red-200 hover:border-red-300 transition-all cursor-pointer group">
          <CardHeader className="text-center">
            <CardTitle className="text-red-700 group-hover:text-red-800">
              🔴 Difficult
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-sm text-gray-600">
              The person is very resistant and will challenge every point. Requires exceptional skill and patience.
            </p>
            <Button 
              onClick={() => onSelectDifficulty("difficult")}
              className="w-full bg-red-600 hover:bg-red-700"
            >
              Start Difficult Mode
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EvangelismAssistantTool;